<script>
/* eslint-disable vue/require-v-for-key */
import { mapActions, mapState, mapStores } from "pinia";
import Tabs from "@/components/Tabs";
import NewSummary from "@/components/NewSummary";
import { format } from "@/libs/date.format";
import Modal from "@/components/Modal";
import EventDetails from "@/components/EventDetails.vue";
import EventDates from "@/components/EventDates.vue";
import NewHolidays from "@/components/NewHolidays";
import CustomCheckbox from "@/components/CustomCheckbox";
import getDatesRange from "@/mixins/getDatesRange";
import RadioInputGroup from "@/components/forms/RadioInputGroup";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import AdvancedDateTime
    from "@/modules/management-panel/components/AddNewEventModal/AdvancedDateTime.vue";
import ViewAllTimesLink from "@/components/ViewAllTimes/ViewAllTimesLink.vue";
import ViewAllTimesModal from "@/components/ViewAllTimes/ViewAllTimesModal.vue";
import { useMainStore } from "@/stores/mainStore";
import { getAgesString } from "../../../../libs/ages.helper.js";
import { post } from "../../../../libs/axios";
import { useBookingSettingsStore } from "../../../../stores/bookingStore";
import TicketsAndPricingV2 from "../../../../components/TicketsAndPriceV2.vue";
import { usePricingStore } from "../../../../stores/pricingStore";
import { getUpcomingEventsAmount, getUpcomingSessions } from "@/components/methods/index.js";
import { isValidEmailFormat } from "../../../../libs/is-valid-email-format";

const IMAGE_BASE_LINK = `${window.imgUrl}${window.businessId}/`;

const EventSeries = {
    name: "EventSeries",
    mixins: [getDatesRange],
    components: {
        TicketsAndPricingV2,
        Tabs,
        NewSummary,
        Modal,
        EventDetails,
        EventDates,
        NewHolidays,
        CustomCheckbox,
        RadioInputGroup,
        ButtonComponent,
        AdvancedDateTime,
        ViewAllTimesLink,
        ViewAllTimesModal,
    },
    data() {
        return {
            fullWeekdays: [
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
            ],
            shortWeekdays: ["sun", "mon", "tue", "wed", "thu", "fri", "sat"],
            currentTab: "summary",
            activity: {},
            editedActivity: {},
            errors: {},
            ages: "",
            mainCategory: "",
            secondaryCategories: "",
            termDate: {},
            session: {},
            showEditDetails: false,
            showEditDates: false,
            confirmShowModal: false,
            editDates: {},
            datesErrors: {},
            showEditHolidays: false,
            editedHolidays: [],
            showEditPrice: false,
            editedPrice: {},
            priceError: "",
            advancedSession: {},
            showDeleteModal: false,
            removeSessionData: "",
            showDeleteEvent: false,
            submittedEditDetails: false,
            submittedEditDates: false,
            submittedEditHolidays: false,
            submittedEditPrice: false,
            showAllTimes: false,
        };
    },

    methods: {
        ...mapActions(useMainStore, [
            "getData",
            "submitSimpleEventDetails",
            "submitSimpleEventPrice",
            "submitSimpleEventDates",
            "submitAdvancedSessions",
            "submitSimpleEventDelete",
            "submitSimpleEventUpdateHolidays",
            "submitSimpleEventRemoveSingleHoliday",
            "submitSimpleEventRevertRemoveSingleHoliday",
        ]),
        async submitRemoveModal() {
            const holiday = {
                termdateId: this.termDate.termdate_id,
                holidayData: format(this.removeSessionData, "yyyy-mm-dd"),
                deleted: true,
            };

            const res = await this.submitSimpleEventRemoveSingleHoliday(
                holiday,
            );

            if (res.data.success) {
                this.showDeleteModal = false;
                this.setData();
            } else {
                alert("Form submitted with errors");
            }
        },
        edit(page) {
            if (page === "details") {
                if (this.getActivitySessions.length > 1) {
                    this.confirmShowModal = "showEditDetails";
                } else {
                    this.showEditDetails = true;
                }
            }

            if (page === "date-time") {
                this.showEditDates = true;
            }

            if (page === "holidays") {
                this.showEditHolidays = true;
                this.editedHolidays = this.getHolidays;
            }

            if (page === "price") {
                this.pricingStore
                    .resetAndFillWithDataForEdit(this.activity.priceV2, this.$storage);
                this.showEditPrice = true;
            }
        },
        setPrice(price) {
            this.editedPrice = { ...this.editedPrice, ...price };
        },
        toggleFreePrice() {
            this.editedPrice.freePricing = !this.editedPrice.freePricing;
        },
        updateEventData(data) {
            this.editDates = data;
        },
        validateEditedDates() {
            this.datesErrors.startDate = this.editDates.dates.start.plain === null
            || this.editDates.dates.start.plain === ""
                ? "Choose a first date"
                : "";

            this.datesErrors.endDate = (this.editDates.dates.end.plain === null
                || this.editDates.dates.end.plain === "")
            && this.editDates.repeatType !== "no-repeat"
                ? "Choose a last date"
                : "";

            this.datesErrors.startTime = this.editDates.times.start === "" ? "Choose a start time" : "";
            this.datesErrors.endTime = this.editDates.times.end === "" ? "Choose an end time" : "";

            const errors = Object.values(this.datesErrors).filter(
                (el) => el !== "",
            ).length;

            return errors <= 0;
        },

        updateDateTimes(data) {
            this.advancedSession = data;
        },

        async submitEditDates() {
            if (!this.isAdvancedRoute) {
                const validForm = this.validateEditedDates();

                if (validForm) {
                    this.editDates.activityId = this.activity.activity_id;
                    this.editDates.termdateId = this.termDate.termdate_id;
                    this.editDates.sessionId = this.session.timetable_id;
                    this.editDates.venueId = this.$route.params.venueId;
                    this.editDates.termName = this.termDate.name;

                    if (!this.submittedEditDates) {
                        this.submittedEditDates = true;

                        const res = await this.submitSimpleEventDates(
                            this.editDates,
                        );

                        this.submittedEditDates = false;

                        if (res.data.success) {
                            this.showEditDates = false;
                            this.setData();
                        } else {
                            alert("Form submitted with errors");
                        }
                    }
                }
            } else {
                const session = this.advancedSession;
                if (this.getAdvancedRepeatType !== "weekly") {
                    session.day = null;
                }

                if (!this.submittedEditDates) {
                    this.submittedEditDates = true;

                    const res = await this.submitAdvancedSessions({
                        activityId: this.activity.activity_id,
                        termdateId: this.termDate.termdate_id,
                        venueId: this.$route.params.venueId,
                        timetableId: this.session.timetable_id,
                        session: this.advancedSession,
                    });

                    this.submittedEditDates = false;

                    if (res.data.success) {
                        if (
                            this.$route.params.weekday
                            !== this.advancedSession.day
                            && this.getAdvancedRepeatType === "weekly"
                        ) {
                            this.$router.replace({
                                name: "Advanced event series",
                                params: {
                                    venueId: this.session.venue_id,
                                    actId: this.activity.activity_id,
                                    termId: this.termDate.termdate_id,
                                    sessionId: this.session.timetable_id,
                                    weekday:
                                        this.advancedSession.day
                                        || this.$route.params.weekday,
                                },
                            });
                        }

                        this.setData();

                        this.showEditDates = false;
                    }
                }
            }
        },
        openDeleteEventModal() {
            this.showDeleteEvent = true;
        },
        async submitDeleteEvent() {
            const data = {
                venueId: this.$route.params.venueId,
                activityId: this.$route.params.actId,
                termDateId: this.termDate.termdate_id,
                sessionId: this.$route.params.sessionId,
            };

            const routeParentName = this.$route.matched[0].name;
            if (routeParentName === "Simple") {
                const numSimpleActivitiesPriorToDelete = this.allActivities
                    .filter((acty) => acty.flow_type === "simple")
                    .length;
                const res = await this.submitSimpleEventDelete(data);

                if (res.data.success) {
                    this.showDeleteEvent = false;
                    await this.setData();
                    if (numSimpleActivitiesPriorToDelete > 1) { // if 1 i'll be zero after delete, only add this if not zero
                        this.$router.push({
                            name: "Simple venue",
                            venueId: this.$route.params.venueId,
                        });
                    } else {
                        this.$router.push({ name: "Simple" });
                    }
                } else {
                    alert("Form submitted with errors");
                }
            } else {
                const result = await post("/remove-advanced-session", {
                    data: {
                        venueId: this.$route.params.venueId,
                        activityId: this.$route.params.actId,
                        termDateId: this.termDate.termdate_id,
                        sessionId: this.$route.params.sessionId,
                    },
                });

                if (result.data.success) {
                    this.showDeleteEvent = false;
                    await this.getData();
                    this.$router.push({
                        name: "Advanced term",
                        params: {
                            termdateId: this.$route.params.termId,
                            venueId: this.$route.params.venueId,
                            weekday: this.$route.params.weekday,
                        },
                    });
                }
            }
        },
        async submitEditDetails() {
            const validForm = this.validateEditedActivity();

            if (validForm && !this.submittedEditDetails) {
                this.submittedEditDetails = true;
                this.editedActivity.activityId = this.activity.activity_id;

                const res = await this.submitSimpleEventDetails(
                    this.editedActivity,
                );

                if (res.data.success) {
                    this.showEditDetails = false;
                    this.submittedEditDetails = false;
                    this.setData();
                } else {
                    if (res.data.errors.activity) {
                        this.errors.name = res.data?.errors?.activity;
                    } else {
                        alert("Form submitted with errors");
                    }
                    this.submittedEditDetails = false;
                }
            }
        },
        async submitEditPrice() {
            if (this.pricingStore.validate()) {
                this.submittedEditPrice = true;

                this.editedPrice = {
                    timetableId: this.session.timetable_id,
                    activityId: this.activity.activity_id,
                    termdateId: this.termDate.termdate_id,
                    venueId: this.$route.params.venueId,
                    freePricing: "",
                    freeTrial: "",
                    price: "",
                    pricePerTerm: "",
                    priceType: "",
                    errors: "",
                };
                try {
                    // Save the old pricing
                    const oldFormat = this.pricingStore.getOldPricingFormat;
                    this.editedPrice.freePricing = oldFormat.freePricing;
                    this.editedPrice.freeTrial = oldFormat.freeTrial;
                    this.editedPrice.price = oldFormat.priceData.price;
                    this.editedPrice.pricePerTerm = oldFormat.priceData.pricePerTerm;
                    this.editedPrice.priceType = oldFormat.priceData.priceType;

                    await this.submitSimpleEventPrice(this.editedPrice);
                    // Save the new pricing
                    await this.pricingStore
                        .saveEventPricing(this.session.id);

                    this.submittedEditPrice = false;
                    this.showEditPrice = false;
                    this.setData();
                } catch (e) {
                    // Old pricing cleaning
                    this.editedPrice.freePricing = this.session.is_free;
                    this.editedPrice.freeTrial = this.session.free_trial;
                    this.editedPrice.price = this.session.price;
                    this.editedPrice.pricePerTerm = this.session.price_per_term;
                    this.editedPrice.priceType = this.session.price_type;
                    await this.submitSimpleEventPrice(this.editedPrice);

                    this.submittedEditPrice = false;
                    console.error("Saving pricing data Failed", e);
                    alert("Saving pricing Failed");
                }
            }
        },
        setUpdatedActivity(activity) {
            this.editedActivity = activity;
        },
        validateEditedActivity() {
            this.errors.name = this.editedActivity.name === "" ? "Enter an event name" : "";

            this.errors.eventType = this.editedActivity.eventType === ""
                ? "Choose an event type"
                : "";

            this.errors.ages = this.editedActivity.ages.length <= 0 ? "Choose an age" : "";

            this.errors.description = this.editedActivity.description === ""
                ? "Enter a description"
                : "";

            this.errors.img = this.editedActivity.img === "" ? "Choose a picture" : "";

            this.errors.primaryCategory = this.editedActivity.primaryCategory === null
                ? "Choose a category"
                : "";

            this.errors.bookingEmail = (this.editedActivity.bookingEmail === ""
                || this.editedActivity.bookingEmail) === null
                || !isValidEmailFormat(this.editedActivity.bookingEmail)
                ? "Enter a booking email"
                : "";

            this.errors.bookingRequired = this.editedActivity.bookingRequired === "required"
                ? "Choose an attendance type"
                : "";

            const errors = Object.values(this.errors).filter(
                (el) => el !== "",
            ).length;

            return errors <= 0;
        },
        async setData() {
            await this.getData();
            await this.setCurrentPageData();
        },
        async setCurrentPageData() {
            if (this.allActivities === "") {
                return;
            }
            const activity = this.allActivities.find(
                (aa) => aa.activity_id === this.$route.params.actId,
            );

            if (activity === null || activity === undefined) {
                const currentVenue = this.allVenues?.find(
                    (v) => v.venue_id === this.$route.params.venueId,
                );

                if (currentVenue === undefined || currentVenue === null) {
                    const firstVenueId = this.allVenues[0]?.venue_id;

                    if (firstVenueId !== undefined) {
                        this.$router.push({
                            name: "Simple venue",
                            params: { venueId: firstVenueId },
                        });
                    }

                    this.$router.push({ name: "Simple" });
                }

                return;
            }

            this.activity = activity;

            const routeName = this.$route.matched[0].name;

            this.termDate = routeName === "Simple"
                ? this.termDates.find(
                    (t) => t.termdate_id === this.$route.params.termId,
                )
                : this.advancedTermDates.find(
                    (t) => t.termdate_id === this.$route.params.termId,
                );

            this.session = this.sessions.find(
                (s) => s.activity_id === this.activity.activity_id
                    && s.timetable_id === this.$route.params.sessionId
                    && s.venue_id === this.$route.params.venueId
                    && s.termdate_id === this.termDate.termdate_id,
            );

            if (this.session.free === "1"
                    || (this.session.free === "0" && this.session.timetable_prices?.length > 0)
            ) {
                const sellingTicket = {
                    ...this.session,
                    prices: [...this.session.timetable_prices],
                };
                this.activity.priceV2 = this.pricingStore
                    .transformApiDataToStateFormat(sellingTicket);
                this.pricingStore.setImmutablePrices(this.activity.priceV2);
                this.activity.showPriceV2 = this.pricingStore.immutablePrices;
                if (this.activity.priceV2?.sellingTicket) {
                    const bookingSettingsHasBusinessId = this.bookingSettingsStore.businessId;
                    const mainStoreAndBookingStoreBusinessIdAreDifferent = this.businessId
                        !== this.bookingSettingsStore.businessId;
                    if (bookingSettingsHasBusinessId
                        && mainStoreAndBookingStoreBusinessIdAreDifferent) {
                        this.bookingSettingsStore.removeLocalStorage(this.$storage);
                    }
                    this.bookingSettingsStore.businessId = this.businessId;
                    this.bookingSettingsStore.getSetBookingSettingsWithStorage(this.$storage);
                }
            }

            this.ages = this.agesForActivities[this.activity.activity_id]
                ? this.agesForActivities[this.activity.activity_id]
                    .map((e) => e[0])
                    .map((el) => ({
                        ...el,
                        // eslint-disable-next-line radix
                        id: parseInt(el.id),
                    }))
                : [];

            const category = this.actCategories.find(
                (ac) => ac.activity_id === this.activity.activity_id
                    && ac.category_type === "0",
            );
            if (category !== undefined) {
                this.mainCategory = this.categories?.find(
                    (c) => c.category_id === category.category_id,
                )?.name;
            } else {
                this.mainCategory = "";
            }

            const secondaryCategories = this.actCategories.filter(
                (ac) => ac.activity_id === this.activity.activity_id
                    && ac.category_type === "1",
            );

            if (secondaryCategories.length > 0) {
                // let secondaryCategoriesIds = secondaryCategories.map(s => s.category_id)

                const secondaryCategoriesNames = secondaryCategories.map(
                    (sc) => {
                        const { name } = this.categories.find(
                            (c) => c.category_id === sc.category_id,
                        );
                        return name;
                    },
                );

                this.secondaryCategories = secondaryCategoriesNames.join(", ");
            } else {
                this.secondaryCategories = "-";
            }
        },
        formatDate(date) {
            if (date?.plain === undefined) {
                return "";
            }

            const day = new Date(date.plain).getDay();
            return `${this.fullWeekdays[day]} ${format(
                date.plain,
                "dS mmmm yyyy",
            )}`;
        },
        formatRawDate(date) {
            if (date === undefined) {
                return "";
            }

            const day = new Date(date).getDay();

            return `${this.fullWeekdays[day]} ${format(date, "dS mmmm yyyy")}`;
        },
        holidaysUpdated(holidays) {
            this.editedHolidays = holidays;
        },
        async submitEditHolidays() {
            const holidays = this.editedHolidays.map((h) => {
                if (h.start.date === null || h.start.date === undefined) {
                    // eslint-disable-next-line no-param-reassign
                    h.start.date = this.formatDateForBackend(h.start.plain);
                }

                if (h.end.date === null || h.end.date === undefined) {
                    // eslint-disable-next-line no-param-reassign
                    h.end.date = this.formatDateForBackend(h.end.plain);
                }

                return h;
            });

            if (!this.submittedEditHolidays) {
                this.submittedEditHolidays = true;

                const res = await this.submitSimpleEventUpdateHolidays({
                    holidays,
                    activityId: this.activity.activity_id,
                    termdateId: this.termDate.termdate_id,
                });

                this.submittedEditHolidays = false;

                if (res.data.success) {
                    this.showEditHolidays = false;
                    this.setData();
                } else {
                    alert("Form submitted with errors");
                }
            }
        },
        formatDateForBackend(date) {
            const d = new Date(date);
            const day = d.getDate();
            const month = d.getMonth() + 1; // getMonth() returns month from 0 to 11
            const year = d.getFullYear();

            return `${day}/${month}/${year}`;
        },
        handleTabChange(tab) {
            this.currentTab = tab.name;
        },
        getHolidayTimes() {
            if (Object.values(this.holidays).length === 0) {
                return [];
            }
            const holidays = this.holidays?.filter(
                (h) => h.termdate_id === this.termDate.termdate_id,
            );

            const holidaysRange = [];
            const deletedRange = [];

            holidays?.forEach((h) => {
                if (h.deleted !== null) {
                    deletedRange.push(
                        this.getDetailsDateRange(new Date(h.start), ""),
                    );
                    return;
                }
                if (h.one_day === "Y") {
                    holidaysRange.push(
                        this.getDetailsDateRange(new Date(h.start), ""),
                    );
                } else {
                    holidaysRange.push(
                        this.getDetailsDateRange(
                            new Date(h.start),
                            new Date(h.end),
                        ),
                    );
                }
            });

            return {
                holidays: holidaysRange,
                deleted: deletedRange,
            };
        },

        setNewActivityImg(img) {
            this.editedActivity.newImg = img;
        },

        getWeekNumOfMonthOfDate(d) {
            const firstDay = new Date(
                d.getFullYear(),
                d.getMonth(),
                1,
            ).getDay();
            return Math.ceil((d.getDate() + (firstDay - 1)) / 7);
        },
        redirectToListingPage() {
            // Generale link for current listing and current location
            let link = `/business/${this.businessSlug}/${this.activity.slug}/${this.$route.params.venueId}`;

            window.open(link, "_blank");
        },
        closeShowAllTimes() {
            this.showAllTimes = false;
        },
    },
    computed: {
        ...mapState(useMainStore, [
            "allActivities",
            "allVenues",
            "categories",
            "allCategories",
            "actVenues",
            "actTermdates",
            "termDates",
            "advancedTermDates",
            "sessions",
            "agesForActivities",
            "actCategories",
            "holidays",
            "businessId",
            "businessSlug",
            "termdateVenuesRel",
            "termdateVenues",
            "IMAGE_BASE_LINK",
        ]),
        ...mapStores(useBookingSettingsStore),
        ...mapStores(usePricingStore),
        getActData() {
            const agesForActivities = this.agesForActivities[this.activity.activity_id] || [];
            const ages = Object.values(agesForActivities).map((el) => el[0]) || [];
            return {
                eventType: this.activity.event_type,
                name: this.activity.title,
                description: this.activity.description,
                ages,
                img: `${this.getBaseImageLink}${this.activity.listing_image_name}`,
                primaryCategory: this.getPrimaryCategory,
                secondaryCategories: this.getSecondaryCategories,
                // secondaryCategories: [],
                bookingEmail: this.activity.booking_email,
                bookingLink: this.activity.booking_link,
                bookingType:
                    this.activity.booking_required === "1"
                        ? "required"
                        : "drop-in",
            };
        },
        getAgesForNewSummary() {
            if (this.ages !== undefined) {
                return getAgesString(this.ages);
            }

            return "";
        },
        getPrimaryCategory() {
            const actCategory = this.actCategories.find(
                (a) => a.activity_id === this.activity.activity_id
                    && a.category_type === "0",
            );
            if (actCategory === undefined) {
                return [];
            }
            return this.categories.find(
                (c) => c.category_id === actCategory.category_id,
            );
        },
        getSecondaryCategories() {
            const actCategories = this.actCategories.find(
                (a) => a.activity_id === this.activity.activity_id
                    && a.category_type === "1",
            );
            if (actCategories === undefined) {
                return [];
            }
            return this.categories.find(
                (c) => c.category_id === actCategories.category_id,
            );
        },
        getHolidays() {
            if (Object.values(this.holidays).length === 0) {
                return [];
            }

            const holidays = this.holidays?.filter(
                (h) => h.termdate_id === this.termDate.termdate_id,
            );

            return holidays?.map((h, idx) => ({
                start: { plain: new Date(h.start) },
                end: { plain: new Date(h.end) },
                oneDay: h.one_day === "Y",
                deleted: h.deleted,
                idx,
            }));
        },
        getRawHolidays() {
            if (Object.values(this.holidays).length === 0) {
                return [];
            }

            const holidays = this.holidays?.filter(
                (h) => h.termdate_id === this.termDate.termdate_id,
            );

            return holidays?.map((h) => {
                const start = new Date(h.start);
                const end = new Date(h.end);

                return {
                    start: this.formatRawDate(start),
                    end: this.formatRawDate(end),
                    oneDay: h.one_day === "Y",
                };
            });
        },
        getSessionDays() {
            return getUpcomingSessions(
                this.session.repetition_type,
                this.termDate,
                this.getHolidayTimes(),
                this.$route.params.weekday,
            );
        },
        getActivityAges() {
            if (this.agesForActivities === undefined) {
                return "";
            }
            const actAges = Object.entries(this.agesForActivities).find(
                ([actId]) => actId === this.$route.params.actId,
            );

            if (actAges !== undefined) {
                if (actAges[1] !== undefined) {
                    return getAgesString(actAges[1]);
                }
            }

            return "";
        },
        getActivitySessions() {
            return this.sessions.filter(
                (session) => session.activity_id === this.activity.activity_id,
            );
        },
        tabs() {
            const sessions = this.getSessionDays;
            return [
                {
                    name: "summary",
                    title: "Summary",
                },
                {
                    name: "dates",
                    title: `Events dates (${sessions.length})`,
                },
            ];
        },

        getBaseImageLink() {
            return IMAGE_BASE_LINK;
        },

        getDefaultRepeatType() {
            if (this.session.repetition_type === "none") {
                return "no-repeat";
            }

            return this.session.repetition_type === "month-w"
            || this.session.repetition_type === "month-d"
                ? "month"
                : this.session.repetition_type;
        },

        getAdvancedRepeatType() {
            return this.session.repetition_type === "week" ? "weekly" : "daily";
        },

        getNewSummaryStartEndDates() {
            const start = new Date(this.termDate.start);
            const end = new Date(this.termDate.end);
            if (
                !Number.isNaN(start.getTime())
                && !Number.isNaN(end.getTime())
            ) {
                let startDate = "";
                let endDate = "";
                if (this.getAdvancedRepeatType === "weekly") {
                    const weekday = this.shortWeekdays.findIndex(
                        (d) => d === this.$route.params.weekday,
                    ) !== -1
                        ? this.shortWeekdays.findIndex((d) => d === this.$route.params.weekday)
                        : start.getDay();

                    startDate = this.getDateForNextWeekDayInRange(
                        weekday,
                        start,
                        end,
                    );
                    endDate = this.getLastWeekDayInRange(
                        weekday,
                        start,
                        end,
                    );
                } else {
                    startDate = start;
                    endDate = end;
                }

                return {
                    start: this.formatRawDate(startDate),
                    end: this.formatRawDate(endDate),
                };
            }

            return "";
        },

        getRouteLink() {
            if (this.$route.matched[0].name === "Simple") {
                return {
                    name: "Simple venue",
                    params: { venueIdx: this.$route.params.venueId },
                };
            }

            return {
                name: "Advanced term",
                params: {
                    termdateId: this.$route.params.termId,
                    venueId: this.$route.params.venueId,
                    weekday: this.$route.params.weekday,
                },
            };
        },
        isAdvancedRoute() {
            return this.$route.matched[0].name === "Advanced";
        },
        getStartSessionTime() {
            return this.session.time_start.replace(" ", "").toLowerCase();
        },
        getEndSessionTime() {
            return this.session.time_finish.replace(" ", "").toLowerCase();
        },
        getActivityVenues() {
            const actTerms = this.actTermdates.reduce(
                (terms, actTerm) => {
                    if (this.session.activity_id === actTerm.activity_id) {
                        return [...terms, actTerm.termdate_id];
                    }
                    return terms;
                },
                [],
            );

            const termDateVenuesIds = this.termdateVenuesRel.reduce(
                (venues, termDateVenue) => {
                    if (actTerms.includes(termDateVenue.termdate_id)) {
                        return [...venues, termDateVenue.venues_id];
                    }

                    return venues;
                },
                [],
            );

            return this.termdateVenues.filter(
                (venue) => termDateVenuesIds.includes(venue.venue_id),
            );
        },
        getAgesForSession() {
            const ages = this.getActivityAges;

            return ages.replace("Ages", "").replace("Years", "");
        },
        getAllTimesSessions() {
            const venuesIds = this.getActivityVenues.map((av) => av.venue_id);

            const filteredSessions = this.sessions?.filter(
                (singleSession) => (
                    singleSession.activity_id === this.activity.activity_id
                    && venuesIds.includes(singleSession.venue_id)
                ),
            );

            return filteredSessions.map((s) => {
                const ages = this.getAgesForSession;
                return {
                    ...s,
                    name: `${this.activity.title} ( ${ages} )`,
                    time: `${s.time_start}-${s.time_finish}`,
                    image: this.IMAGE_BASE_LINK + this.activity.listing_image_name,
                };
            });
        },
        getAllEventsAmount() {
            return getUpcomingEventsAmount(
                new Date(this.termDate.start),
                new Date(this.termDate.end),
                this.session.repetition_type,
                this.holidays,
            );
        },
        isListingPageLinkAvailable() {
            return this.businessSlug && this.activity.slug && this.$route.params.venueId;
        },
    },
    async created() {
        await this.getData();
        this.setCurrentPageData();
    },
    watch: {
        showEditPrice(oldValue, newValue) {
            if (oldValue && !newValue) {
                this.pricingStore.reset(this.$storage);
            }
        },
    },
};

export default EventSeries;
</script>

<template>
    <div class="spacer-block back-link">
        <router-link :to="getRouteLink" class="my-3 back-to-venues">
            &lt; Back
        </router-link>

        <div class="mt-5 d-flex flex-row">
            <div class="align-self-center mr-2">
                <img
                    alt="activity image"
                    class="medium-img"
                    :src="getBaseImageLink + activity.listing_image_name"
                />
            </div>
            <div class="d-flex flex-column">
                <h5 class="act-title" v-html="activity?.title"></h5>
            </div>
        </div>

        <div class="w-full d-flex flex-row justify-content-between my-5 tab-bar-header-container">
            <div class="session-tabs mt-3">
                <Tabs :tabs="tabs" class="" @change="handleTabChange"/>
            </div>
            <div class="view-published-listing-container">
                <ButtonComponent
                    @click="redirectToListingPage"
                    class="view-published-listing-btn"
                    type="outline"
                    :disabled="!isListingPageLinkAvailable"
                >
                    View published listing
                </ButtonComponent>
            </div>
        </div>

        <div v-if="currentTab === 'summary'">
            <NewSummary
                :activity="{
                    eventType: activity.event_type,
                    description: activity.description,
                    bookingEmail: activity.booking_email,
                    bookingLink: activity.booking_link,
                    bookingType:
                        activity.booking_required === '1'
                            ? 'required'
                            : 'drop-in',
                    name: activity.title,
                }"
                :mainCategory="mainCategory"
                :secondaryCategory="secondaryCategories"
                :ages="getAgesForNewSummary"
                :img="getBaseImageLink + activity.listing_image_name"
                :dates="{
                    start: getNewSummaryStartEndDates.start,
                    end: getNewSummaryStartEndDates.end,
                    startRaw: this.termDate.start,
                    endRaw: this.termDate.end,
                    repeat:
                        this.session.repetition_type === 'month-w'
                            ? 'month'
                            : this.session.repetition_type,
                }"
                :times="{
                    start: this.session.time_start,
                    end: this.session.time_finish,
                }"
                :holidays="getRawHolidays"
                :addHoliday="isAdvancedRoute ? 'yes' : 'no'"
                :forceShowHolidays="isAdvancedRoute ? false : true"
                :price="{
                    price: session.price,
                    pricePerTerm:
                        session.price_per_term === '0.00'
                            ? ''
                            : session.price_per_term,
                    freeTrial: session.free_trial === '1' ? 'Yes' : 'No',
                    freePricing: session.is_free === '1',
                }"
                :priceV2="activity.showPriceV2"
                @edit-click="edit($event)"
            />
        </div>
        <div v-if="currentTab === 'dates'" class="pt-5">
            <div
                v-for="(day, dIdx) in getSessionDays"
                :key="dIdx"
                class="d-flex flex-row event-date mb-3"
                :class="{ 'deleted-event-date': day.deleted }"
            >
                <div class="justify-content-start align-self-center day-date">
                    {{ day.date > 9 ? day.date : `0${day.date}` }}
                </div>
                <div class="d-flex flex-column day-info">
                    <div class="d-flex flex-row mt-3 day-info_title">
                        <div class="mr-2">{{ day.month }}</div>
                        <div>{{ day.year }}</div>
                    </div>
                    <div class="session-info">
                        {{ day.weekday }}
                        &nbsp;•&nbsp;
                        {{ getStartSessionTime }}
                        -
                        {{ getEndSessionTime }}
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="currentTab === 'summary'" class="mt-5">
        <ButtonComponent @click="openDeleteEventModal" type="warning" dataTest="btn-delete-event">
            Delete event
        </ButtonComponent>
    </div>

    <Modal
        v-if="confirmShowModal"
        :show-modal="confirmShowModal"
        @close-modal="confirmShowModal = false"
        @submit-modal="
            this[confirmShowModal] = true;
            confirmShowModal = false;
        "
        submit-text="Continue"
        title="Warning"
        :show-spinner="showHeaderSpinner"
        :remove-modal="true"
        modal-dialog-class="modal-md"
        close-text="Cancel"
        close-btn-class="primary"
    >
        This will update the details for ALL sessions for event:
        <strong>
            {{ activity?.title }}
            {{ `${activity?.event_type?.name || activity?.event_type}` }}.
        </strong>
        <br/><br/>
        <ul>
            <li v-for="session in getActivitySessions">
                <span
                    v-if="session.repetition_type === 'day'"
                    class="text-capitalize"
                >Daily</span
                >
                <span
                    v-if="session.repetition_type === 'week'"
                    class="text-capitalize"
                >{{ session.day }}s</span
                >
                {{ session.time_start }} - {{ session.time_finish }}
            </li>
        </ul>
    </Modal>

    <Modal
        v-if="showEditDetails"
        :show-modal="showEditDetails"
        :show-spinner="submittedEditDetails"
        @close-modal="showEditDetails = false"
        @submit-modal="submitEditDetails"
    >
        <h2 class="my-3">Event details</h2>
        <EventDetails
            :default="getActData"
            :default-categories="allCategories"
            @set-img="setNewActivityImg"
            @updated="setUpdatedActivity"
            :errors="errors"
        />
    </Modal>

    <Modal
        v-if="showEditDates"
        :show-modal="showEditDates"
        :show-spinner="submittedEditDates"
        @close-modal="showEditDates = false"
        @submit-modal="submitEditDates"
    >
        <EventDates
            v-if="!isAdvancedRoute"
            :event-type="activity.event_type?.name || activity?.event_type"
            :default-dates="{
                start: { plain: termDate.start },
                end: { plain: termDate.end },
            }"
            :default-times="{
                start: session.time_start,
                end: session.time_finish,
            }"
            :default-holidays="
                holidays?.filter(
                    (h) => h.termdate_id === this.termDate.termdate_id
                )
            "
            :default-repeat-type="getDefaultRepeatType"
            :default-month-repeat-type="
                session.repetition_type === 'month-w' ? 'weekday' : 'day'
            "
            :errors="datesErrors"
            @updated="updateEventData"
            isEditMode="true"
        />
        <AdvancedDateTime
            v-if="isAdvancedRoute"
            :event-type="activity.event_type?.name"
            :start="new Date(termDate.start)"
            :end="new Date(termDate.end)"
            :startTime="session.time_start"
            :endTime="session.time_finish"
            :errors="''"
            :recurrence="getAdvancedRepeatType"
            :day="$route.params.weekday"
            @updated="updateDateTimes"
        />
    </Modal>

    <Modal
        v-if="showEditHolidays"
        :show-modal="showEditHolidays"
        :show-spinner="submittedEditHolidays"
        @close-modal="showEditHolidays = false"
        @submit-modal="submitEditHolidays"
    >
        <h2 class="my-3">Holidays</h2>

        <NewHolidays
            :default-holidays="getHolidays"
            :start-date="termDate.start"
            :end-date="termDate.end"
            @updated="holidaysUpdated"
        />
    </Modal>

    <Modal
        v-if="showEditPrice"
        :show-modal="showEditPrice"
        :show-spinner="submittedEditPrice"
        @close-modal="showEditPrice = false"
        @submit-modal="submitEditPrice"
    >
        <TicketsAndPricingV2
            :event-type="activity.event_type?.name || activity.event_type"
            :recurring-type="session.repetition_type"
            :all-events-amount="getAllEventsAmount"
        />
    </Modal>

    <Modal
        v-if="showDeleteModal"
        :show-modal="showDeleteModal"
        @close-modal="showDeleteModal = false"
        @submit-modal="submitRemoveModal"
        modal-dialog-class="modal-md"
        close-text="Cancel"
        submit-text="Delete"
        submit-btn-class="delete-button"
    >
        Are you sure you want to delete this date?
    </Modal>

    <Modal
        v-if="showDeleteEvent"
        :show-modal="showDeleteEvent"
        @close-modal="showDeleteEvent = false"
        @submit-modal="submitDeleteEvent"
        modal-dialog-class="modal-md"
        submit-text="Delete"
        submit-btn-class="delete-button"
        :title="'<h5>Delete event series</h5>'"
    >
        <div class="pr-4">
            Are you sure you want to delete the
            {{ activity.title }} series? This will delete
            <b>{{ this.getSessionDays.length }}</b> dates.
        </div>
    </Modal>

    <ViewAllTimesModal
        :show-modal="showAllTimes"
        :venues="getActivityVenues"
        :sessions="getAllTimesSessions"
        :terms="termDates"
        :event-name="`${activity.title} ( ${getAgesForSession} )`"
        :header-text="`All ${activity.title} times`"
        @close="closeShowAllTimes"
    />
</template>

<style scoped>
.session-tabs {
    max-width: 270px;
}

.session-info {
    font-style: normal;
    font-weight: 400;
    font-size: 0.874rem;
    line-height: 1.32rem;
    color: #636066;
}

.act-title {
    font-weight: 600;
}

.back-to-venues {
    display: block;
    margin-top: 1.44rem !important;
}

.day-info_title {
    font-weight: 600;
}

.day-info {
    margin-left: 0.5rem;
}

@media only screen and (max-width: 768px) {
    .tab-bar-header-container {
        flex-wrap: wrap;
    }

    .view-published-listing-container {
        width: 100%;
    }

    .view-published-listing-btn {
        margin-top: 20px;
        width: 100%;
    }
}
</style>
